import React from 'react';
import SmallDoughnut from 'common/components/SmallDoughnut';
import { LayoutMode, SnapshotNode } from './DashboardTeam.utils';
import {
    NodeWrapper,
    NodeCard,
    NodeBox,
    TitleBar,
    TitleText,
    Meta,
    MetricsRow,
    MetricBadge,
    MetricDot,
    DoughnutBubble,
    DoughnutWrapper,
    ChildrenRow,
    TreeChildrenColumn,
    TreeChildItem,
    TreeParentConnector,
    CollapseButton,
    CollapseIcon,
    HorizontalLine,
} from './DashboardTeam.styles';
import { scoreToColor, lightenHex } from './DashboardTeam.utils';

interface DashboardTeamNodeProps {
    node: SnapshotNode;
    childrenMap: Map<number, SnapshotNode[]>;
    visited?: Set<number>;
    onOpen?: (id: number) => void;
    leaderId?: number | null;
    collapsedNodeIds: Set<number>;
    onToggleCollapse?: (id: number) => void;
    layoutMode: LayoutMode;
    compactView: boolean;
}

export const renderNode = (
    node: SnapshotNode,
    childrenMap: Map<number, SnapshotNode[]>,
    visited: Set<number> | undefined,
    onOpen: ((id: number) => void) | undefined,
    leaderId: number | null | undefined,
    collapsedNodeIds: Set<number>,
    onToggleCollapse: ((id: number) => void) | undefined,
    layoutMode: LayoutMode,
    compactView: boolean
): React.ReactNode => {
    const visitedSet = visited ?? new Set<number>();
    if (visitedSet.has(node.id)) return null;
    const nextVisited = new Set(visitedSet);
    nextVisited.add(node.id);

    const children = childrenMap.get(node.id) || [];
    const isCollapsed = collapsedNodeIds.has(node.id);
    const hasTeamScore = node.overallScore != null && !Number.isNaN(Number(node.overallScore));
    const showTeamScoreRow = !compactView && (hasTeamScore || leaderId == null);
    const showSubTeamRow = !compactView && node.extendedOverallScore != null && !Number.isNaN(node.extendedOverallScore);
    const compactTooltip = compactView
        ? [
            hasTeamScore ? `Team Score: ${Number(node.overallScore).toFixed(1)}` : 'Team Score: N/A',
            node.extendedOverallScore != null && !Number.isNaN(Number(node.extendedOverallScore))
                ? `Sub-Teams: ${Number(node.extendedOverallScore).toFixed(1)}`
                : null,
          ].filter(Boolean).join('\n')
        : undefined;

    const clickableAllowed = (hasTeamScore || leaderId == null) && typeof onOpen === 'function';

    return (
        <NodeWrapper key={node.id} data-testid={`team-node-${node.id}`} $layoutMode={layoutMode}>
            <NodeCard>
                <NodeBox
                    $layoutMode={layoutMode}
                    className="org-node-box"
                    title={compactTooltip}
                    onClick={clickableAllowed ? () => onOpen && onOpen(node.id) : undefined}
                    style={{ cursor: clickableAllowed ? 'pointer' : 'default' }}
                >
                    {(() => {
                        const barMetric = node.extendedOverallScore ?? node.overallScore ?? null;
                        const rawColor = barMetric != null ? scoreToColor(barMetric) : '#d1d5db';
                        const barColor = barMetric != null ? lightenHex(rawColor, 0.5) : '#d1d5db';
                        return <TitleBar $color={barColor}><TitleText>{node.name}</TitleText></TitleBar>;
                    })()}
                    {node.leader_name && <Meta $compactView={compactView}>{node.leader_name}</Meta>}

                    <div style={{ marginTop: compactView ? 0 : 8, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            {showTeamScoreRow ? (
                                <MetricsRow style={{ justifyContent: 'flex-start' }}>
                                    <MetricBadge>
                                        <MetricDot $bg={hasTeamScore ? scoreToColor(node.overallScore as number) : '#d1d5db'} />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>Team Score</div>
                                            {hasTeamScore ? (
                                                <div style={{ fontWeight: 700 }}>{Number(node.overallScore).toFixed(1)}</div>
                                            ) : (
                                                <div style={{ fontWeight: 400, color: '#6b7280' }}>N/A</div>
                                            )}
                                        </div>
                                    </MetricBadge>
                                </MetricsRow>
                            ) : null}

                            {showSubTeamRow ? (
                                <MetricsRow style={{ justifyContent: 'flex-start' }}>
                                    <MetricBadge>
                                        <MetricDot $bg={scoreToColor(node.extendedOverallScore!)} />
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                            <div style={{ fontSize: 12, color: '#6b7280' }}>Sub-Teams</div>
                                            <div style={{ fontWeight: 700 }}>{Number(node.extendedOverallScore).toFixed(1)}</div>
                                        </div>
                                    </MetricBadge>
                                </MetricsRow>
                            ) : null}
                        </div>
                    </div>

                    {showTeamScoreRow ? (
                        <DoughnutBubble>
                            <DoughnutWrapper size={46}>
                                <SmallDoughnut value={node.overallScore ?? 0} colorized size={42} />
                            </DoughnutWrapper>
                        </DoughnutBubble>
                    ) : null}
                </NodeBox>

                {children.length > 0 && (
                    <CollapseButton
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleCollapse?.(node.id);
                        }}
                        aria-label={isCollapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
                        title={isCollapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
                    >
                        <CollapseIcon $hideOnDefault={!isCollapsed}>{isCollapsed ? '+' : '-'}</CollapseIcon>
                    </CollapseButton>
                )}
                {!isCollapsed && children.length > 0 && layoutMode === 'tree' && <TreeParentConnector />}
            </NodeCard>

            {!isCollapsed && children.length > 0 && (
                layoutMode === 'tree' ? (
                    <TreeChildrenColumn $compactView={compactView}>
                        {children.map(child => (
                            <TreeChildItem key={child.id}>
                                {renderNode(child, childrenMap, nextVisited, onOpen, leaderId, collapsedNodeIds, onToggleCollapse, layoutMode, compactView)}
                            </TreeChildItem>
                        ))}
                    </TreeChildrenColumn>
                ) : (
                    <ChildrenRow>
                        <HorizontalLine />
                        {children.map(child => (
                            <div key={child.id} style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', top: -18, left: '50%', width: 2, height: 18, background: '#e6e6e6', transform: 'translateX(-50%)', zIndex: 1 }} />
                                {renderNode(child, childrenMap, nextVisited, onOpen, leaderId, collapsedNodeIds, onToggleCollapse, layoutMode, compactView)}
                            </div>
                        ))}
                    </ChildrenRow>
                )
            )}
        </NodeWrapper>
    );
};

export const DashboardTeamNode = (props: DashboardTeamNodeProps) => renderNode(
    props.node,
    props.childrenMap,
    props.visited ?? new Set<number>(),
    props.onOpen,
    props.leaderId,
    props.collapsedNodeIds,
    props.onToggleCollapse,
    props.layoutMode,
    props.compactView,
);

export default DashboardTeamNode;
